import React from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Table, Title, Divider } from '@mantine/core';
import { IconExclamationCircle } from '@tabler/icons-react';

import { DashboardHeader, Loader } from '../../../components/common';
import { GET_MASTER_DATA_IMPORT_ERRORS } from '../../../apollo/queries/masterDataImportErrors';
import { useAuth } from '../../../context/AuthProvider';

function MasterDataImportErrors() {
    const { role } = useAuth();
    const { data, loading } = useQuery(GET_MASTER_DATA_IMPORT_ERRORS, {
        skip: role === 'storemanager',
    });

    if (role === 'storemanager') {
        return <Navigate to="/price-change/requests" replace />;
    }

    const procurementErrors =
        data?.getMasterDataErrors.procurementHierarchyErrors?.map(
            (error: string) => ({ error }),
        ) ?? [];

    const operationErrors =
        data?.getMasterDataErrors.operationHierarchyErrors?.map(
            (error: string) => ({ error }),
        ) ?? [];

    const productErrors =
        data?.getMasterDataErrors.productImportErrors?.map((error: string) => ({
            error,
        })) ?? [];

    const renderErrorRows = (errors: { error: string }[]) => {
        return errors.map((element, index) => (
            <React.Fragment key={element.error}>
                <tr>
                    <td className="py-3">
                        {index + 1}.&#9;
                        {element.error}
                    </td>
                </tr>
                {index < errors.length - 1 && (
                    <tr>
                        <td className="p-0" aria-hidden="true">
                            <Divider />
                        </td>
                    </tr>
                )}
            </React.Fragment>
        ));
    };

    return (
        <div className="relative">
            <DashboardHeader
                title="Master Data Import Errors"
                friendlyLabels={[
                    {
                        originalLabel: 'price-change',
                        friendlyLabel: 'Price Change',
                    },
                    {
                        originalLabel: 'master-data-import-errors',
                        friendlyLabel: 'Master Data Import Errors',
                    },
                ]}
            />
            {loading ? (
                <Loader isPageLoader />
            ) : (
                <div className="w-full flex flex-col gap-5 px-6 pb-8">
                    {procurementErrors.length > 0 && (
                        <div className="w-full bg-white">
                            <div className="w-full flex flex-col px-6 py-3">
                                <div className="flex items-center gap-3">
                                    <IconExclamationCircle color="red" />
                                    <Title size="h3">
                                        Procurement Hierarchy Errors
                                    </Title>
                                </div>
                                <Table>
                                    <tbody>
                                        {renderErrorRows(procurementErrors)}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    )}

                    {operationErrors.length > 0 && (
                        <div className="w-full bg-white">
                            <div className="w-full flex flex-col px-6 py-3">
                                <div className="flex items-center gap-3">
                                    <IconExclamationCircle color="red" />
                                    <Title size="h3">
                                        Operation Hierarchy Errors
                                    </Title>
                                </div>
                                <Table>
                                    <tbody>
                                        {renderErrorRows(operationErrors)}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    )}

                    {productErrors.length > 0 && (
                        <div className="w-full bg-white">
                            <div className="w-full flex flex-col px-6 py-3">
                                <div className="flex items-center gap-3">
                                    <IconExclamationCircle color="red" />
                                    <Title size="h3">
                                        Product Import Errors
                                    </Title>
                                </div>
                                <Table>
                                    <tbody>
                                        {renderErrorRows(productErrors)}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default MasterDataImportErrors;
